import React from 'react';
import { Trash2, ChevronUp, ChevronDown, CircleCheck, Plus, X } from 'lucide-react';
import RichTextField from './RichTextField';

// Редактор одного вопроса. Контролируемый: всё состояние живёт у родителя,
// чтобы было одно «деревянное» дерево без разъезжающихся стейтов.
const QuestionEditor = ({
  index,
  total,
  question,
  onChange,
  onMoveUp,
  onMoveDown,
  onRemove,
}) => {
  const setField = (patch) => onChange({ ...question, ...patch });
  const setOption = (i, text) => {
    const opts = question.options.map((o, k) => (k === i ? { ...o, text } : o));
    setField({ options: opts });
  };
  const addOption = () => {
    if (question.options.length >= 8) return;
    setField({ options: [...question.options, { text: '' }] });
  };
  const removeOption = (i) => {
    if (question.options.length <= 2) return;
    const opts = question.options.filter((_, k) => k !== i);
    let correct = question.correctOptionIndex;
    if (i === correct) correct = 0;
    else if (i < correct) correct -= 1;
    setField({ options: opts, correctOptionIndex: correct });
  };

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 min-w-8 items-center justify-center rounded-lg bg-indigo-600 px-2 text-sm font-semibold text-white tabular-nums">
            {index + 1}
          </span>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Вопрос {index + 1}
          </h3>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={index === 0}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40"
            aria-label="Выше"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={index === total - 1}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40"
            aria-label="Ниже"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
            aria-label="Удалить"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <RichTextField
        value={question.text}
        onChange={(text) => setField({ text })}
        rows={2}
        maxLength={1000}
        placeholder="Текст вопроса. Поддерживается **жирный**, _курсив_, формулы $x^2 + y^2 = z^2$"
      />

      <div className="mt-4 space-y-2">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>Варианты ответа · отметьте правильный</span>
          <span>{question.options.length} / 8</span>
        </div>
        {question.options.map((opt, i) => {
          const isCorrect = i === question.correctOptionIndex;
          return (
            <div
              key={i}
              className={`flex items-center gap-2 rounded-xl border px-3 py-2 transition ${
                isCorrect
                  ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/20'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'
              }`}
            >
              <button
                type="button"
                onClick={() => setField({ correctOptionIndex: i })}
                className={`shrink-0 rounded-full p-1 transition ${
                  isCorrect ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-300 dark:text-gray-600 hover:text-emerald-500'
                }`}
                aria-label={isCorrect ? 'Правильный ответ' : 'Сделать правильным'}
                title={isCorrect ? 'Правильный ответ' : 'Сделать правильным'}
              >
                <CircleCheck className="w-5 h-5" />
              </button>
              {/* Вариант ответа — тоже rich, можно писать формулы (например
                  выбор из вариантов уравнения). Inline-режим без многострочности. */}
              <div className="flex-1 min-w-0">
                <RichTextField
                  value={opt.text}
                  onChange={(text) => setOption(i, text)}
                  maxLength={300}
                  multiline={false}
                  placeholder={`Вариант ${i + 1}`}
                />
              </div>
              {question.options.length > 2 && (
                <button
                  type="button"
                  onClick={() => removeOption(i)}
                  className="p-1 rounded-md text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-red-500"
                  aria-label="Удалить вариант"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
        {question.options.length < 8 && (
          <button
            type="button"
            onClick={addOption}
            className="inline-flex items-center gap-1.5 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <Plus className="w-4 h-4" /> Добавить вариант
          </button>
        )}
      </div>

      <div className="mt-4">
        <RichTextField
          value={question.explanation || ''}
          onChange={(text) => setField({ explanation: text })}
          maxLength={500}
          multiline={false}
          placeholder="Пояснение к ответу (необязательно, видно после сдачи)"
        />
      </div>
    </div>
  );
};

export default QuestionEditor;
